import { useEffect, useMemo, useRef } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { ArrowForward, AtOutline, TeachMode } from '@lyearn/icons';
import { Editor } from '@lyearn/lygo-core';
import { cn } from '@lyearn/style';
import { Box, Button, DeprecatedIconButton, LinkButton, Stack, Typography } from '@lyearn/ui';
import { FeatureFlags, useFeatureFlags } from '@/helper/FeatureFlags';
import { useUserContext } from '@/modules/auth/hooks/useUser';

import useCoachInput from '../../hooks/useCoachInput';
import {
  height100vhMinusHeader,
  iconButtonStyles,
  lightPurpleBg,
  maxWidth768,
  mobileHeightAdjustment,
  placeholderColor,
  purpleBorder,
  questionButtonStyles,
} from '../../styles';
import { getSuggestedCoachTopics } from '../../utils';
import AiWarning from '../AiWarning';
import { mentionHelperStyles } from '../ChatView/styles';
import Greeting from '../Greeting';

interface InitialChatInputProps {
  message: string;
  onMessageChange: (value: string) => void;
  onEnter: (text: string) => void;
  onClickStarterQuestion: (question: string) => void;
  onClickSend: (firstMessage: boolean) => void;
  teachModeEnabled: boolean;
  toggleTeachMode: () => void;
}

export const InitialChatInput = ({
  message,
  onMessageChange,
  onEnter,
  onClickStarterQuestion,
  onClickSend,
  teachModeEnabled,
  toggleTeachMode,
}: InitialChatInputProps) => {
  const { t } = useTranslation();
  const inputContainerRef = useRef<HTMLInputElement>(null);
  const user = useUserContext();
  const { [FeatureFlags.EnableKnowledgeBaseTeachMode]: isTeachModeFeatureEnabled } =
    useFeatureFlags();

  const handleInputChange = (editorMessage: string, textContent: string) => {
    onMessageChange(editorMessage);
  };

  const placeholder = useMemo(() => t('Common:KnowledgeBase.KnowledgeBaseInputPlaceholder'), [t]);

  const { editorProps, mentionsButtonProps, autocomplete } = useCoachInput({
    onInputChange: handleInputChange,
    placeholder,
    onEnter,
    useAutocomplete: true,
  });

  const { suggestions, onSuggestionClick } = autocomplete;

  // TODO: suggestedCoachTopics are hardcoded. Delete this before rolling out teach mode.
  const suggestedCoachTopics = useMemo(() => getSuggestedCoachTopics(), []);
  const { suggestedQuestionForCoach } = user;

  const suggestedStarterQuestions = useMemo(() => {
    if (suggestedQuestionForCoach?.length) {
      return suggestedQuestionForCoach.map((question) => ({
        value: question,
      }));
    }
    return [];
  }, [suggestedQuestionForCoach]);

  useEffect(() => {
    if (inputContainerRef.current) {
      const inputElement = inputContainerRef.current.querySelector('textarea');
      inputElement?.focus();
    }
  }, [inputContainerRef.current]);

  return (
    <Stack
      className={cn(
        maxWidth768,
        'w-full mx-auto px-[80px] xs:px-24 overflow-y-auto overflow-x-hidden relative',
        height100vhMinusHeader,
        mobileHeightAdjustment,
      )}
      direction="col"
      gapY={24}
      justifyContent="center">
      {/* <Stack alignItems="center" direction="col">
        <LyearnLogoFill height={40} width={40} />
        <Typography className="mt-8" variant="body-short-01">
          {chatbot?.name || t('Common:KnowledgeBase.LyearnCoach')}
        </Typography>
      </Stack> */}
      <Greeting teachModeEnabled={teachModeEnabled} />

      <Box className="relative w-full">
        <Stack
          className={cn(
            'p-12 rounded-16 border shadow-02 text-14',
            placeholderColor,
            mentionHelperStyles,
            teachModeEnabled ? purpleBorder : 'border-border',
            teachModeEnabled ? lightPurpleBg : 'bg-bg-primary',
          )}
          direction="col"
          gapY={24}>
          <Editor {...editorProps} />

          <Stack
            alignItems="center"
            direction="row"
            justifyContent={isTeachModeFeatureEnabled ? 'between' : 'end'}>
            {isTeachModeFeatureEnabled ? (
              <Button
                appearance="secondary"
                className={cn(teachModeEnabled ? 'bg-bg-purple-light' : '')}
                size="x-small"
                startIcon={
                  <TeachMode
                    className="max-h-[16px] max-w-[16px]"
                    fillColorToken="transparent"
                    height={16}
                    width={16}
                  />
                }
                variant="outlined"
                onClick={toggleTeachMode}>
                <Typography variant="body-short-02">{t('Channel:TeachMode')}</Typography>
              </Button>
            ) : null}
            <Stack gap={8}>
              <LinkButton
                className={cn(iconButtonStyles, 'py-2 px-4')}
                color={'internal'}
                onClick={mentionsButtonProps.onMentionButtonClick}>
                <AtOutline className="fill-current" />
              </LinkButton>
              <DeprecatedIconButton
                appearance={!message.length ? 'secondary' : 'primary'}
                className={'mt-auto'}
                disabled={!message.length}
                size="x-small"
                variant={!message.length ? 'ghost' : 'filled'}
                onClick={() => onClickSend(true)}>
                <ArrowForward className="fill-current rtl:rotate-180" />
              </DeprecatedIconButton>
            </Stack>
          </Stack>
        </Stack>

        {suggestions.length > 0 && (
          <Stack
            className={cn(
              'absolute z-10 bg-bg-primary w-full mt-4 p-8 rounded-8 shadow-01 border border-border',
            )}
            direction="col">
            {suggestions.map((suggestion, index) => {
              const inputValue = message.trim().toLowerCase();
              const matchIndex = suggestion.toLowerCase().indexOf(inputValue);
              const matchLength = inputValue.length;

              return (
                <Stack key={index} className="px-12 py-[9px] rounded-8 hover:bg-bg-hover">
                  <Typography
                    className={cn('px-4 py-2 cursor-pointer', 'truncate')}
                    variant="body-short-02"
                    onClick={() => onSuggestionClick(suggestion)}>
                    {matchIndex >= 0 ? (
                      <>
                        <Typography className="text-text-secondary">
                          {suggestion.substring(0, matchLength)}
                        </Typography>
                        <Typography>{suggestion.substring(matchLength)}</Typography>
                      </>
                    ) : (
                      <Typography>{suggestion}</Typography>
                    )}
                  </Typography>
                </Stack>
              );
            })}
          </Stack>
        )}
      </Box>

      {teachModeEnabled ? (
        <Stack className="grid grid-cols-3 gap-16">
          {suggestedCoachTopics.map((topic, index) => (
            <Box
              key={index}
              className={cn('cursor-pointer border border-border rounded-12 px-16 py-12 group')}
              onClick={() => onClickStarterQuestion(topic.value)}>
              <Typography
                className="group-hover:text-text-primary"
                color="text-text-secondary"
                variant="body-short-02">
                {topic.value}
              </Typography>
            </Box>
          ))}
        </Stack>
      ) : suggestedStarterQuestions?.length ? (
        <Stack className={cn('mt-16 mb-[2.4rem]')} direction="col">
          <Typography className="text-text-secondary mb-12 ms-[1.1rem]" variant="label-03">
            {t('Common:KnowledgeBase.SuggestedQuestions')}
          </Typography>
          <Stack className="flex-wrap" direction="row" gap={12}>
            {suggestedStarterQuestions.map((question, index) => (
              <Button
                key={index}
                fullWidth
                alignment="left"
                appearance="secondary"
                className={cn('!p-[1.1rem] h-auto w-fit border-none', questionButtonStyles)}
                shape="square"
                variant="outlined"
                onClick={() => onClickStarterQuestion(question.value)}>
                <Typography className="text-left" variant="body-short-03">
                  {question.value}
                </Typography>
              </Button>
            ))}
          </Stack>
        </Stack>
      ) : null}
      <AiWarning className="absolute bottom-12 start-1/2 -translate-x-1/2 rtl:translate-x-1/2 w-full" />
    </Stack>
  );
};
